import React from 'react';
import { Clock, Play, CheckCircle, AlertCircle, Mail, FileText, Zap } from 'lucide-react';

const SchedulerTab = ({
  schedulerStatus, schedulerLogs, triggering, triggerResult, handleTrigger,
  blogSchedulerStatus, blogTriggering, blogTriggerResult, handleBlogTrigger,
}) => (
  <div className="scheduler-section" data-testid="scheduler-dashboard">
    {/* Blog Auto-Scheduler — Daily Article */}
    <div className="scheduler-status-card" data-testid="blog-scheduler-card" style={{ marginBottom: '1.5rem' }}>
      <div className="scheduler-status-header">
        <h2 className="section-heading" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FileText size={20} /> Artikel Harian (Auto-Scheduler)
        </h2>
        <span className={`scheduler-badge ${blogSchedulerStatus?.active ? 'active' : 'inactive'}`}>
          {blogSchedulerStatus?.active ? 'Active' : 'Inactive'}
        </span>
      </div>
      <div className="scheduler-meta">
        <div className="scheduler-meta-item">
          <Clock size={16} />
          <span>Jadwal: {blogSchedulerStatus?.schedule || 'Setiap hari @ 07:00 WIB'}</span>
        </div>
        <div className="scheduler-meta-item">
          <Zap size={16} color="#d9fb06" />
          <span>1 artikel per hari, template bergantian, auto-publish + eksklusif 14 hari</span>
        </div>
        {blogSchedulerStatus?.current_time_wib && (
          <div className="scheduler-meta-item">
            <Clock size={16} />
            <span>Waktu sekarang: {blogSchedulerStatus.current_time_wib}</span>
          </div>
        )}
        {blogSchedulerStatus?.next_run && (
          <div className="scheduler-meta-item">
            <CheckCircle size={16} color="#4ade80" />
            <span>Artikel berikutnya: {new Date(blogSchedulerStatus.next_run).toLocaleString('id-ID', { weekday: 'long', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' })} WIB</span>
          </div>
        )}
      </div>
      {blogSchedulerStatus?.last_result && (
        <div className="scheduler-last-result">
          <h4>Hasil Generate Terakhir:</h4>
          {blogSchedulerStatus.last_result.error ? (
            <p className="scheduler-error">Error: {blogSchedulerStatus.last_result.error}</p>
          ) : blogSchedulerStatus.last_result.status === 'published' ? (
            <div className="scheduler-result-stats" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.25rem' }}>
              <span style={{ color: '#4ade80' }}>Berhasil publish!</span>
              <span>Judul: <strong>{blogSchedulerStatus.last_result.title}</strong></span>
              <span>Template: {blogSchedulerStatus.last_result.template}</span>
              <span style={{ fontSize: '0.75rem', color: '#71717A' }}>
                {blogSchedulerStatus.last_result.timestamp ? new Date(blogSchedulerStatus.last_result.timestamp).toLocaleString('id-ID') : ''}
              </span>
            </div>
          ) : blogSchedulerStatus.last_result.status === 'failed' ? (
            <p className="scheduler-error">Gagal generate. Template: {blogSchedulerStatus.last_result.template || '-'}</p>
          ) : null}
        </div>
      )}
      <button
        className="scheduler-trigger-btn"
        onClick={handleBlogTrigger}
        disabled={blogTriggering}
        data-testid="blog-trigger-btn"
      >
        <Play size={18} /> {blogTriggering ? 'Memulai...' : 'Generate Artikel Sekarang'}
      </button>
      {blogTriggerResult && (
        <div className={`scheduler-trigger-result ${blogTriggerResult.status === 'started' ? 'success' : 'warning'}`}>
          {blogTriggerResult.status === 'started'
            ? blogTriggerResult.message
            : blogTriggerResult.message || 'Gagal trigger generate'}
        </div>
      )}
    </div>

    {/* Email Scheduler */}
    <div className="scheduler-status-card" data-testid="scheduler-status-card">
      <div className="scheduler-status-header">
        <h2 className="section-heading" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Mail size={20} /> Email Scheduler
        </h2>
        <span className={`scheduler-badge ${schedulerStatus?.active ? 'active' : 'inactive'}`}>
          {schedulerStatus?.active ? 'Active' : 'Inactive'}
        </span>
      </div>
      <div className="scheduler-meta">
        <div className="scheduler-meta-item">
          <Clock size={16} />
          <span>Interval: setiap {schedulerStatus?.interval_minutes || 30} menit</span>
        </div>
        <div className="scheduler-meta-item">
          {schedulerStatus?.sendgrid_configured ? (
            <><CheckCircle size={16} color="#4ade80" /><span>SendGrid terkonfigurasi</span></>
          ) : (
            <><AlertCircle size={16} color="#f87171" /><span>SendGrid belum dikonfigurasi</span></>
          )}
        </div>
        {schedulerStatus?.last_run && (
          <div className="scheduler-meta-item">
            <Mail size={16} />
            <span>Run terakhir: {new Date(schedulerStatus.last_run).toLocaleString()}</span>
          </div>
        )}
      </div>
      {schedulerStatus?.last_result && (
        <div className="scheduler-last-result">
          <h4>Hasil Run Terakhir:</h4>
          {schedulerStatus.last_result.skipped ? (
            <p className="scheduler-warning">Dilewati — {schedulerStatus.last_result.reason === 'sendgrid_not_configured' ? 'SendGrid belum dikonfigurasi' : schedulerStatus.last_result.reason}</p>
          ) : schedulerStatus.last_result.error ? (
            <p className="scheduler-error">Error: {schedulerStatus.last_result.error}</p>
          ) : (
            <div className="scheduler-result-stats">
              <span>{schedulerStatus.last_result.subscribers_checked || 0} subscriber dicek</span>
              <span>{schedulerStatus.last_result.emails_sent || 0} email terkirim</span>
              {schedulerStatus.last_result.errors > 0 && (
                <span className="scheduler-error">{schedulerStatus.last_result.errors} error</span>
              )}
            </div>
          )}
        </div>
      )}
      <button className="scheduler-trigger-btn" onClick={handleTrigger} disabled={triggering} data-testid="scheduler-trigger-btn">
        <Play size={18} /> {triggering ? 'Menjalankan...' : 'Trigger Email Manual'}
      </button>
      {triggerResult && (
        <div className={`scheduler-trigger-result ${triggerResult.status === 'completed' ? 'success' : 'warning'}`}>
          {triggerResult.status === 'completed'
            ? `Selesai: ${triggerResult.result?.emails_sent || 0} email terkirim, ${triggerResult.result?.subscribers_checked || 0} subscriber dicek`
            : triggerResult.reason}
        </div>
      )}
    </div>

    {schedulerLogs?.stages && schedulerLogs.stages.length > 0 && (
      <div className="scheduler-stages" data-testid="scheduler-stages">
        <h2 className="section-heading">Progres Email Sequence</h2>
        <div className="stages-grid">
          {schedulerLogs.stages.map((stage) => (
            <div key={stage.stage} className="stage-card">
              <div className="stage-number">{stage.stage}</div>
              <div className="stage-info">
                <h4>{stage.name}</h4>
                <p className="stage-count">{stage.count} subscriber</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {schedulerLogs?.logs && schedulerLogs.logs.length > 0 && (
      <div className="data-table" data-testid="scheduler-logs">
        <h2 className="section-heading">Log Email Terkirim</h2>
        <table>
          <thead><tr><th>Email</th><th>Stage</th><th>Subscribed</th><th>Email Terakhir</th></tr></thead>
          <tbody>
            {schedulerLogs.logs.map((log, i) => (
              <tr key={i}>
                <td>{log.email}</td>
                <td><span className="badge">Email {log.email_sequence_status}/4</span></td>
                <td>{log.subscribed_at ? new Date(log.subscribed_at).toLocaleDateString() : '-'}</td>
                <td>{log.last_email_sent ? new Date(log.last_email_sent).toLocaleString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {(!schedulerLogs?.logs || schedulerLogs.logs.length === 0) && (
      <div className="scheduler-empty">
        <Mail size={48} />
        <p>Belum ada email sequence yang terkirim.</p>
        <p className="scheduler-hint">Email nurturing akan dikirim otomatis sesuai jadwal setelah subscriber mendaftar dan SendGrid terkonfigurasi.</p>
      </div>
    )}
  </div>
);

export default SchedulerTab;
